import { Globe } from '@phosphor-icons/react';
import { SVGProps, useContext, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import DropdownMenuItem from '@tmh-website/ui/DropdownMenuItem';
import type { Page } from '@tmh-website/shared/cms';
import { cleanLink } from '@tmh-website/services/link.service';
import { Color } from '@tmh-website/shared/colors';
import { FlagCH, FlagDE, FlagFR, FlagINT, FlagUS } from '@tmh-website/components/assets';
import { LocalePrefix, PrefixToLocaleMap, Project } from '@tmh-website/shared/const';
import { PageContext, ProjectContext } from '@tmh-website/shared';
import { type FormElementThemeColor } from '@tmh-website/ui/FormElements';

import EnvironmentSelectButton from '../EnvironmentSelectButton';

import * as Styled from './LocaleSelect.styles';
import { AVAILABLE_LOCALES, ENERGY_LOCALES } from './LocaleSelect.config';

const localeToFlagMap: { [key in LocalePrefix]: FC<SVGProps<SVGSVGElement>> } = {
  [LocalePrefix.En]: FlagINT,
  [LocalePrefix.De]: FlagDE,
  [LocalePrefix.DeCH]: FlagCH,
  [LocalePrefix.EnUS]: FlagUS,
  [LocalePrefix.FrCH]: FlagCH,
  [LocalePrefix.Fr]: FlagFR,
};

/**
 * Redirect to the page in a different locale variant.
 * @param page The current page
 * @param locale The current locale
 * @param targetLocale The locale to redirect to
 */
export function redirectToLocaleVariant(page, locale: LocalePrefix, targetLocale) {
  const foundLocalization = ((page?.localizations ?? []) as unknown as Page[]).find(
    localization => localization.locale === PrefixToLocaleMap[targetLocale]
  );

  let path: string;
  if (foundLocalization) {
    path = foundLocalization.path;
  } else {
    path = '/';
  }

  if (locale === targetLocale && typeof window !== 'undefined') {
    return;
  }

  window.location.href = cleanLink(
    // The env variable name is different on the magento-header
    `${process.env.NEXT_PUBLIC_ORIGIN ?? process.env.NX_NEXT_PUBLIC_ORIGIN}/${targetLocale}${path}`
  );
}

export interface LocaleSelectProps {
  /**
   * Set a custom selected locale.
   * This is used for the LocaleSwitchBanner which displays the recommended locale.
   */
  customLabelLocale?: LocalePrefix;

  /**
   * Add a custom on select function which receives the new locale prefix.
   * Return true to continue with the default behaviour and false to stop it.
   */
  customOnSelect?: (locale: LocalePrefix) => boolean;

  isSmall?: boolean;
  locale: LocalePrefix;
  themeColor: FormElementThemeColor;
}

const LocaleSelect = ({
  locale,
  themeColor,
  isSmall,
  customOnSelect,
  customLabelLocale,
  ...props
}: LocaleSelectProps) => {
  const page = useContext(PageContext);
  const { project } = useContext(ProjectContext);
  const { t } = useTranslation();

  const availableLocales: LocalePrefix[] = project === Project.Energy ? ENERGY_LOCALES : AVAILABLE_LOCALES;

  return (
    <EnvironmentSelectButton
      data-cy='localeSelectButton'
      themeColor={themeColor}
      isSmall={isSmall}
      label={
        <Styled.Label>
          {PrefixToLocaleMap[customLabelLocale ?? locale].toUpperCase()}{' '}
          <span>({t(`global:locale.${customLabelLocale ?? locale}`).split(' ')[0]})</span>
        </Styled.Label>
      }
      iconLeft={!isSmall && <Globe size={24} />}
      {...props}>
      {availableLocales.map(localePrefix => {
        const Flag = localeToFlagMap[localePrefix];
        return (
          <DropdownMenuItem
            data-cy='localeSelectItem'
            key={localePrefix}
            themeColor={Color.Neutral_200_OffWhite}
            // TODO we might be able to do this using the next router, but this won't work in "standalone" mode for the magento header.
            onSelect={() => {
              if (!customOnSelect || customOnSelect(localePrefix)) {
                redirectToLocaleVariant(page, locale, localePrefix);
              }
            }}>
            {/**
             * If an existing localization for this page exists, use it.
             * Otherwise link to the homepage in the other locale.
             */}
            <Styled.Entry $isSelected={localePrefix === locale}>
              <>
                <Flag />
                <span>{t(`global:locale.${localePrefix}`)}</span>
              </>
            </Styled.Entry>
          </DropdownMenuItem>
        );
      })}
    </EnvironmentSelectButton>
  );
};

export default LocaleSelect;
